import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Link, Navigate } from "react-router-dom";
import Logo from "../components/Logo";
import {
  Users,
  DollarSign,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Search,
  ChevronLeft,
  ExternalLink,
  Ban,
  Edit2,
  Eye,
  Download,
  Settings,
  AlertTriangle
} from "lucide-react";

const ADMIN_EMAILS = ["reynir@replydriven.com"];

const AdminAffiliates = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, active: 0, rejected: 0, disabled: 0 });
  const [commissionSummary, setCommissionSummary] = useState({});
  const [activeTab, setActiveTab] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [approveModal, setApproveModal] = useState({ open: false, affiliate: null });
  const [rejectModal, setRejectModal] = useState({ open: false, affiliate: null });
  const [viewModal, setViewModal] = useState({ open: false, affiliate: null });
  const [paidModal, setPaidModal] = useState({ open: false, commission: null });
  
  // Form states
  const [approveForm, setApproveForm] = useState({ commission_percent: 30, code: "", send_email: true });
  const [rejectForm, setRejectForm] = useState({ reason: "", send_email: true });
  const [paidForm, setPaidForm] = useState({ payout_reference: "" });
  const [codeAvailable, setCodeAvailable] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check if user is admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      const [affRes, commRes] = await Promise.all([
        api.get(`/admin/affiliates${activeTab !== "commissions" ? `?status=${activeTab === "approved" ? "active" : activeTab}` : ""}`),
        activeTab === "commissions" ? api.get("/admin/affiliate-commissions") : Promise.resolve({ data: { commissions: [], summary: {} } })
      ]);
      
      setAffiliates(affRes.data.affiliates || []);
      setCounts(affRes.data.counts || {});
      
      if (activeTab === "commissions") {
        setCommissions(commRes.data.commissions || []);
        setCommissionSummary(commRes.data.summary || {});
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load affiliate data");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check code availability with debounce
  useEffect(() => {
    if (!approveForm.code || approveForm.code.length < 2) {
      setCodeAvailable(null);
      return;
    }
    
    const timer = setTimeout(async () => {
      setCheckingCode(true);
      try {
        const res = await api.get(`/admin/affiliates/check-code/${approveForm.code}`);
        setCodeAvailable(res.data.available);
      } catch {
        setCodeAvailable(null);
      } finally {
        setCheckingCode(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [approveForm.code]);

  const handleApprove = async () => {
    if (!approveModal.affiliate) return;
    setSubmitting(true);
    
    try {
      await api.post(`/admin/affiliates/${approveModal.affiliate.affiliate_id}/approve`, {
        commission_percent: approveForm.commission_percent,
        code: approveForm.code || undefined,
        send_email: approveForm.send_email
      });
      
      toast.success("Affiliate approved!");
      setApproveModal({ open: false, affiliate: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.affiliate) return;
    setSubmitting(true);
    
    try {
      await api.post(`/admin/affiliates/${rejectModal.affiliate.affiliate_id}/reject`, {
        reason: rejectForm.reason,
        send_email: rejectForm.send_email
      });
      
      toast.success("Affiliate rejected");
      setRejectModal({ open: false, affiliate: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!paidModal.commission) return;
    setSubmitting(true);
    
    try {
      await api.post(`/admin/affiliate-commissions/${paidModal.commission.commission_id}/mark-paid`, {
        payout_reference: paidForm.payout_reference
      });
      
      toast.success("Commission marked as paid");
      setPaidModal({ open: false, commission: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoidCommission = async (commissionId) => {
    if (!confirm("Are you sure you want to void this commission?")) return;
    
    try {
      await api.post(`/admin/affiliate-commissions/${commissionId}/void`);
      toast.success("Commission voided");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to void");
    }
  };

  const handleExportCSV = () => {
    // Export current filtered view
    let dataToExport = [];
    let filename = "";
    
    if (activeTab === "commissions") {
      // Export commissions
      dataToExport = commissions.map(c => ({
        affiliate_email: c.affiliate_email || "",
        affiliate_code: c.affiliate_code || "",
        user_email: c.user_email || "",
        plan: c.plan || "",
        amount: c.amount || 0,
        commission_amount: c.commission_amount || 0,
        status: c.status || "",
        created_at: c.created_at || ""
      }));
      filename = `commissions_${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      // Export affiliates for current tab
      dataToExport = affiliates.map(a => ({
        email: a.email || "",
        name: a.name || "",
        code: a.code || "",
        status: a.status || "",
        commission_percent: a.commission_percent || 30,
        paypal_email: a.paypal_email || "",
        total_referrals: a.stats?.total_referrals || 0,
        total_earned: a.stats?.total_earned || 0,
        created_at: a.created_at || ""
      }));
      filename = `affiliates_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    }
    
    if (dataToExport.length === 0) {
      toast.error("No data to export");
      return;
    }
    
    // Convert to CSV
    const headers = Object.keys(dataToExport[0]);
    const csvContent = [
      headers.join(","),
      ...dataToExport.map(row => 
        headers.map(h => {
          const val = row[h];
          // Escape quotes and wrap in quotes if contains comma
          if (typeof val === "string" && (val.includes(",") || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        }).join(",")
      )
    ].join("\n");
    
    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${dataToExport.length} rows`);
  };

  const handleDisable = async (affiliateId) => {
    if (!confirm("Are you sure you want to disable this affiliate?")) return;
    
    try {
      await api.post(`/admin/affiliates/${affiliateId}/disable`);
      toast.success("Affiliate disabled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disable");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount || 0);
  };

  const openApproveModal = (affiliate) => {
    setApproveForm({
      commission_percent: affiliate.commission_percent || 30,
      code: affiliate.code || "",
      send_email: true
    });
    setCodeAvailable(null);
    setApproveModal({ open: true, affiliate });
  };

  const openRejectModal = (affiliate) => {
    setRejectForm({ reason: "", send_email: true });
    setRejectModal({ open: true, affiliate });
  };

  const openViewModal = async (affiliate) => {
    try {
      const res = await api.get(`/admin/affiliates/${affiliate.affiliate_id}`);
      setViewModal({ open: true, affiliate: res.data });
    } catch {
      toast.error("Failed to load affiliate details");
    }
  };

  // Filter affiliates by search
  const filteredAffiliates = affiliates.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return a.name?.toLowerCase().includes(q) ||
           a.email?.toLowerCase().includes(q) ||
           a.code?.toLowerCase().includes(q);
  });

  // Redirect if not admin
  if (!user) return <Navigate to="/" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;

  const tabs = [
    { id: "pending", label: "Pending", count: counts.pending },
    { id: "approved", label: "Approved", count: counts.active },
    { id: "rejected", label: "Rejected", count: counts.rejected },
    { id: "commissions", label: "Commissions", count: null }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo showText linkTo="/" />
            <span className="text-text-muted">/</span>
            <Link to="/dashboard" className="text-text-muted hover:text-white text-sm">Dashboard</Link>
            <span className="text-text-muted">/</span>
            <span className="text-white text-sm font-medium">Admin</span>
          </div>
          
          <Link to="/dashboard">
            <Button variant="ghost" className="text-text-muted hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
        </div>
      </nav>

      {/* Admin Sub-Navigation */}
      <div className="fixed top-[65px] left-0 right-0 z-40 bg-[#0a0a0a] border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1">
            <Link
              to="/admin"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Overview
            </Link>
            <Link
              to="/admin/affiliates"
              className="px-4 py-3 text-sm font-medium border-b-2 border-[#D4F34A] text-white"
            >
              Affiliates
            </Link>
            <Link
              to="/admin/users"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Users
            </Link>
            <Link
              to="/admin/customers"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Customers
            </Link>
            <Link
              to="/admin/payouts"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Payouts
            </Link>
          </div>
        </div>
      </div>

      <main className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold">Affiliates</h1>
              <p className="text-text-muted mt-1">Manage partners and commissions.</p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="outline"
                className="border-[#262626] text-white hover:bg-white/5"
                onClick={handleExportCSV}
              >
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 border-b border-[#262626]">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-[#D4F34A] text-white"
                    : "border-transparent text-text-muted hover:text-white"
                }`}
              >
                {tab.label}
                {tab.count !== null && tab.count > 0 && (
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                    activeTab === tab.id ? "bg-[#D4F34A] text-black" : "bg-[#262626]"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Commissions Summary Cards */}
          {activeTab === "commissions" && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-surface border-[#262626]">
                <CardContent className="py-4">
                  <p className="text-text-muted text-sm">Unpaid Commission</p>
                  <p className="text-2xl font-bold text-[#D4F34A]">{formatCurrency(commissionSummary.total_unpaid)}</p>
                </CardContent>
              </Card>
              <Card className="bg-surface border-[#262626]">
                <CardContent className="py-4">
                  <p className="text-text-muted text-sm">This Month</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(commissionSummary.this_month)}</p>
                </CardContent>
              </Card>
              <Card className="bg-surface border-[#262626]">
                <CardContent className="py-4">
                  <p className="text-text-muted text-sm">Total Paid Out</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(commissionSummary.total_paid)}</p>
                </CardContent>
              </Card>
              <Card className="bg-surface border-[#262626]">
                <CardContent className="py-4">
                  <p className="text-text-muted text-sm">Pending Approvals</p>
                  <p className="text-2xl font-bold text-white">{commissionSummary.pending_count || 0}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Search */}
          {activeTab !== "commissions" && (
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <Input
                placeholder="Search by name, email, or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-surface border-[#262626] text-white"
              />
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
            </div>
          ) : activeTab === "commissions" ? (
            /* Commissions Table */
            <div className="bg-surface border border-[#262626] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#1a1a1a]">
                  <tr>
                    <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Date</th>
                    <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Affiliate</th>
                    <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Customer</th>
                    <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Plan</th>
                    <th className="text-right p-4 text-xs font-medium text-text-muted uppercase">Amount</th>
                    <th className="text-right p-4 text-xs font-medium text-text-muted uppercase">Commission</th>
                    <th className="text-center p-4 text-xs font-medium text-text-muted uppercase">Status</th>
                    <th className="text-right p-4 text-xs font-medium text-text-muted uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262626]">
                  {commissions.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-text-muted">
                        No commissions yet
                      </td>
                    </tr>
                  ) : commissions.map(comm => (
                    <tr key={comm.commission_id} className="hover:bg-white/5">
                      <td className="p-4 text-sm">{formatDate(comm.created_at)}</td>
                      <td className="p-4">
                        <p className="font-medium text-white">{comm.affiliate_name}</p>
                        <p className="text-xs text-text-muted">{comm.affiliate_code}</p>
                      </td>
                      <td className="p-4 text-sm text-text-muted">{comm.user_email || comm.user_id?.slice(0, 12)}</td>
                      <td className="p-4 text-sm capitalize">{comm.plan}</td>
                      <td className="p-4 text-sm text-right">{formatCurrency(comm.amount)}</td>
                      <td className="p-4 text-sm text-right font-medium text-[#D4F34A]">{formatCurrency(comm.commission_amount)}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          comm.status === "paid" ? "bg-green-500/20 text-green-400" :
                          comm.status === "approved" ? "bg-blue-500/20 text-blue-400" :
                          comm.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                          "bg-gray-500/20 text-gray-400"
                        }`}>
                          {comm.status}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {comm.status !== "paid" && comm.status !== "void" && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setPaidForm({ payout_reference: "" });
                                setPaidModal({ open: true, commission: comm });
                              }}
                              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 text-xs"
                            >
                              Mark Paid
                            </Button>
                          )}
                          {comm.status !== "void" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleVoidCommission(comm.commission_id)}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              Void
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* Affiliates Table */
            <div className="bg-surface border border-[#262626] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#1a1a1a]">
                  <tr>
                    <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Applicant</th>
                    <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">PayPal</th>
                    <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Applied</th>
                    <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Code</th>
                    {activeTab === "approved" && (
                      <>
                        <th className="text-right p-4 text-xs font-medium text-text-muted uppercase">Signups</th>
                        <th className="text-right p-4 text-xs font-medium text-text-muted uppercase">Revenue</th>
                        <th className="text-right p-4 text-xs font-medium text-text-muted uppercase">Owed</th>
                      </>
                    )}
                    <th className="text-center p-4 text-xs font-medium text-text-muted uppercase">Status</th>
                    <th className="text-right p-4 text-xs font-medium text-text-muted uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262626]">
                  {filteredAffiliates.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === "approved" ? 9 : 6} className="p-8 text-center text-text-muted">
                        No affiliates found
                      </td>
                    </tr>
                  ) : filteredAffiliates.map(aff => (
                    <tr key={aff.affiliate_id} className="hover:bg-white/5">
                      <td className="p-4">
                        <p className="font-medium text-white">{aff.name}</p>
                        <p className="text-xs text-text-muted">{aff.email}</p>
                      </td>
                      <td className="p-4 text-sm text-text-muted">{aff.payout_email || "—"}</td>
                      <td className="p-4 text-sm text-text-muted">{formatDate(aff.created_at)}</td>
                      <td className="p-4">
                        {aff.code ? (
                          <div className="flex items-center gap-2">
                            <code className="text-sm text-[#D4F34A]">{aff.code}</code>
                            <button onClick={() => copyToClipboard(aff.code)} className="text-text-muted hover:text-white">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-text-muted">(auto on approve)</span>
                        )}
                      </td>
                      {activeTab === "approved" && (
                        <>
                          <td className="p-4 text-sm text-right">{aff.referred_count || 0}</td>
                          <td className="p-4 text-sm text-right">{formatCurrency(aff.total_revenue_referred)}</td>
                          <td className="p-4 text-sm text-right font-medium text-[#D4F34A]">{formatCurrency(aff.pending_amount)}</td>
                        </>
                      )}
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          aff.status === "active" ? "bg-green-500/20 text-green-400" :
                          aff.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                          aff.status === "rejected" ? "bg-red-500/20 text-red-400" :
                          "bg-gray-500/20 text-gray-400"
                        }`}>
                          {aff.status === "active" ? "Approved" : aff.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {activeTab === "pending" && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => openApproveModal(aff)}
                                className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 text-xs"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openRejectModal(aff)}
                                className="text-red-400 hover:text-red-300 text-xs"
                              >
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {activeTab === "approved" && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDisable(aff.affiliate_id)}
                                className="text-text-muted hover:text-white text-xs"
                              >
                                <Ban className="w-3 h-3 mr-1" /> Disable
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openViewModal(aff)}
                            className="text-text-muted hover:text-white text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Approve Modal */}
      <Dialog open={approveModal.open} onOpenChange={(open) => !open && setApproveModal({ open: false, affiliate: null })}>
        <DialogContent className="bg-[#171717] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Approve Affiliate</DialogTitle>
            <DialogDescription className="text-text-muted">
              Approve {approveModal.affiliate?.name} as an affiliate partner.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Commission %</label>
              <Input
                type="number"
                value={approveForm.commission_percent}
                onChange={(e) => setApproveForm({ ...approveForm, commission_percent: parseFloat(e.target.value) || 30 })}
                className="bg-[#1a1a1a] border-[#262626] text-white"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Referral Code</label>
              <div className="relative">
                <Input
                  value={approveForm.code}
                  onChange={(e) => setApproveForm({ ...approveForm, code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  placeholder={approveModal.affiliate?.code || "Auto-generated"}
                  className="bg-[#1a1a1a] border-[#262626] text-white pr-20"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                  {checkingCode ? (
                    <Loader2 className="w-3 h-3 animate-spin text-text-muted" />
                  ) : codeAvailable === true ? (
                    <span className="text-green-400">Available ✓</span>
                  ) : codeAvailable === false ? (
                    <span className="text-red-400">Taken ✗</span>
                  ) : null}
                </span>
              </div>
            </div>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={approveForm.send_email}
                onChange={(e) => setApproveForm({ ...approveForm, send_email: e.target.checked })}
                className="rounded border-[#262626] bg-[#1a1a1a]"
              />
              <span className="text-sm text-text-muted">Send approval email with referral link</span>
            </label>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveModal({ open: false, affiliate: null })} className="text-text-muted">
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={submitting || codeAvailable === false}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Approve & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={(open) => !open && setRejectModal({ open: false, affiliate: null })}>
        <DialogContent className="bg-[#171717] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Reject Affiliate
            </DialogTitle>
            <DialogDescription className="text-text-muted">
              Reject {rejectModal.affiliate?.name}&apos;s application.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Reason (optional)</label>
              <Input
                value={rejectForm.reason}
                onChange={(e) => setRejectForm({ ...rejectForm, reason: e.target.value })}
                placeholder="Not included in email unless specified"
                className="bg-[#1a1a1a] border-[#262626] text-white"
              />
            </div>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rejectForm.send_email}
                onChange={(e) => setRejectForm({ ...rejectForm, send_email: e.target.checked })}
                className="rounded border-[#262626] bg-[#1a1a1a]"
              />
              <span className="text-sm text-text-muted">Send rejection email</span>
            </label>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectModal({ open: false, affiliate: null })} className="text-text-muted">
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={submitting}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Modal */}
      <Dialog open={paidModal.open} onOpenChange={(open) => !open && setPaidModal({ open: false, commission: null })}>
        <DialogContent className="bg-[#171717] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Mark Commission as Paid</DialogTitle>
            <DialogDescription className="text-text-muted">
              Commission: {formatCurrency(paidModal.commission?.commission_amount)} to {paidModal.commission?.affiliate_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Payout Reference (optional)</label>
              <Input
                value={paidForm.payout_reference}
                onChange={(e) => setPaidForm({ ...paidForm, payout_reference: e.target.value })}
                placeholder="PayPal transaction ID, etc."
                className="bg-[#1a1a1a] border-[#262626] text-white"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPaidModal({ open: false, commission: null })} className="text-text-muted">
              Cancel
            </Button>
            <Button
              onClick={handleMarkPaid}
              disabled={submitting}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Affiliate Modal */}
      <Dialog open={viewModal.open} onOpenChange={(open) => !open && setViewModal({ open: false, affiliate: null })}>
        <DialogContent className="bg-[#171717] border-[#262626] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewModal.affiliate?.name}</DialogTitle>
            <DialogDescription className="text-text-muted">{viewModal.affiliate?.email}</DialogDescription>
          </DialogHeader>
          
          {viewModal.affiliate && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-text-muted uppercase mb-1">Status</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    viewModal.affiliate.status === "active" ? "bg-green-500/20 text-green-400" :
                    viewModal.affiliate.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-red-500/20 text-red-400"
                  }`}>
                    {viewModal.affiliate.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-text-muted uppercase mb-1">Commission</p>
                  <p className="text-white">{viewModal.affiliate.commission_percent}%</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted uppercase mb-1">PayPal Email</p>
                  <p className="text-white">{viewModal.affiliate.payout_email || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted uppercase mb-1">Applied</p>
                  <p className="text-white">{formatDate(viewModal.affiliate.created_at)}</p>
                </div>
              </div>
              
              {viewModal.affiliate.status === "active" && (
                <>
                  <div>
                    <p className="text-xs text-text-muted uppercase mb-2">Referral Link</p>
                    <div className="flex items-center gap-2 bg-[#1a1a1a] border border-[#262626] rounded-lg p-3">
                      <code className="text-sm text-[#D4F34A] flex-1 truncate">{viewModal.affiliate.referral_link}</code>
                      <button onClick={() => copyToClipboard(viewModal.affiliate.referral_link)} className="text-text-muted hover:text-white">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-[#1a1a1a] rounded-lg p-3">
                      <p className="text-xs text-text-muted">Signups</p>
                      <p className="text-xl font-bold text-white">{viewModal.affiliate.referred_count}</p>
                    </div>
                    <div className="bg-[#1a1a1a] rounded-lg p-3">
                      <p className="text-xs text-text-muted">Active Subs</p>
                      <p className="text-xl font-bold text-white">{viewModal.affiliate.active_subscriptions}</p>
                    </div>
                    <div className="bg-[#1a1a1a] rounded-lg p-3">
                      <p className="text-xs text-text-muted">Revenue</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(viewModal.affiliate.total_revenue_referred)}</p>
                    </div>
                    <div className="bg-[#1a1a1a] rounded-lg p-3">
                      <p className="text-xs text-text-muted">Earned</p>
                      <p className="text-xl font-bold text-[#D4F34A]">{formatCurrency(viewModal.affiliate.total_earned)}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setViewModal({ open: false, affiliate: null })} className="text-text-muted">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAffiliates;
